// import { useWeb3React } from '@web3-react/core';
import useSWR from 'swr';
import { useEffect } from 'react';
import { config } from '../../../config/config';
import {
    // EnabledChains,
    OrderBookParamStructs,
    OrderBookType,
    OrderDataTypes,
} from '../../../config/constants';
import { formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import useDelayLoading from '../../useDelayLoading';
import { BigNumber } from 'ethers';
// import useTokenDetails from '../useTokenDetails';
import useUserPositions, { UserPositionRaw } from './useUserPositions';
import useTradePools from './useTradePools';
import useUserFee from '../useUserFee';
import useUserOrders from './useUserOrders';
// import { isPendingOrder } from '../../../helpers';

export type UserOrder = UserPositionRaw & {
    orderData: OrderDataTypes<OrderBookParamStructs>;
    orderId: string;
    orderType: OrderBookType;
    deadline: number;
    limitPrice: string;
};

let initialLoadUp = false;

const useUserOpenOrders = () => {
    // const { chainId } = useWeb3React();

    const delayComplete = useDelayLoading();

    // const { decimals } = useTokenDetails(
    //     config.addresses[chainId as EnabledChains[number]]?.collateral
    // );

    const { userPositions } = useUserPositions();
    const { userOrders } = useUserOrders();
    const { tradePools } = useTradePools();
    const { userFee } = useUserFee();

    const { data, error, mutate } = useSWR(
        userOrders &&
            // && decimals
            userPositions &&
            tradePools &&
            userFee
            ? [
                  'userOpenOrders',
                  userOrders,
                  // decimals,
                  userPositions,
                  tradePools,
                  userFee,
              ]
            : null,
        async (
            _,
            orders,
            // decimals,
            userPositions,
            tradePools,
            userFee
        ): Promise<UserOrder[] | undefined> => {
            const userOpenOrders = orders
                .filter(({ orderJson, status, isMarketOrder }) => {
                    return true;
                    // if (!isPendingOrder(status) || isMarketOrder) return false;

                    // const orderData: OrderDataTypes<OrderBookParamStructs> =
                    //     JSON.parse(orderJson);

                    // if ('positionId' in orderData.params) {
                    //     const positionId = orderData.params.positionId.toString();
                    //     const position = userPositions.find(
                    //         ({ id }) => id === positionId
                    //     );

                    //     return !!position;
                    // }

                    // return true;
                })
                .sort((a, b) => b.createdAt - a.createdAt)
                .map(({ isShort, orderType, liquidationPrice, orderJson, id, type }) => {
                    const orderData: OrderDataTypes<OrderBookParamStructs> =
                        JSON.parse(orderJson);

                    const deadline = Number(orderData.constraints.deadline);

                    let price: BigNumber;

                    if (type === OrderBookType.TAKE_PROFIT) {
                        if (isShort) {
                            price = BigNumber.from(orderData.constraints.maxPrice);
                        } else {
                            price = BigNumber.from(orderData.constraints.minPrice);
                        }
                    } else {
                        price = BigNumber.from(orderData.constraints.minPrice)
                            .add(BigNumber.from(orderData.constraints.maxPrice))
                            .div(2);
                    }

                    const limitPrice = formatUnits(price, config.orderBookDecimal);

                    if (type) {
                        orderType = type;
                    }

                    if ('positionId' in orderData.params) {
                        const positionId = orderData.params.positionId.toString();

                        const positionDetails = userPositions.find(
                            ({ id }) => id === positionId
                        );

                        return {
                            ...positionDetails!,
                            orderData,
                            orderId: id,
                            orderType,
                            deadline,
                            limitPrice,
                        };
                    }

                    const leverage = BigNumber.from(orderData.params.leverage)
                        .div(config.leverageMultiplier)
                        .toString();

                    const margin = formatUnits(
                        BigNumber.from(orderData.params.margin)
                            .mul(parseUnits('1', 18))
                            .div(
                                parseUnits('1', 18).add(
                                    parseUnits(leverage, 18).mul(userFee).div(100_00)
                                )
                            ),
                        18
                    );

                    const entryMargin = margin;

                    const currentVolume = formatUnits(
                        BigNumber.from(leverage).mul(parseUnits(margin, 18)),
                        18
                    );

                    const tradePair = tradePools.find(
                        ({ address }) =>
                            address.toLowerCase() ===
                            orderData.params.tradePair.toString().toLowerCase()
                    )!;

                    // '0' values here are just placeholder to match type, they SHOULD not be used
                    return {
                        orderId: id,
                        orderType,
                        deadline,
                        orderData,
                        tradePair,
                        id: '',
                        margin,
                        volume: currentVolume,
                        size: '0',
                        leverage,
                        entryLeverage: leverage,
                        isShort,
                        entryPrice: '0',
                        entryMargin,
                        markPrice: '0',
                        bankruptcyPrice: liquidationPrice,
                        equity: '0',
                        totalFeeAmount: '0',
                        borrowFee: '0',
                        fundingFee: '0',
                        currentVolume,
                        limitPrice,
                        tpsl: {
                            stopLoss: undefined,
                            takeProfit: undefined,
                        },
                        openedTimestamp: 0,
                    };
                });

            return userOpenOrders.filter(({ margin }) => !parseEther(margin).isZero());
        },
        {
            dedupingInterval: 2000,
        }
    );

    useEffect(() => {
        if (delayComplete) {
            initialLoadUp = true;
        }
    }, [delayComplete]);

    return {
        userOrders: data,
        isLoading: (!data && !error) || (!initialLoadUp && !delayComplete),
        error,
        updateUserOpenOrders: mutate,
    };
};

export default useUserOpenOrders;
